import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Grid, Card, Stack, Skeleton, Chip, Alert, Button, Tabs, Tab,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, ToggleButton, ToggleButtonGroup,
  IconButton, Tooltip, alpha, useTheme,
} from '@mui/material';
import {
  MessageCircle, MessagesSquare, Reply, Timer, Inbox, Send, Trophy, FileText, RefreshCw, ArrowRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import KpiTile from '../components/charts/KpiTile';
import AreaTrendCard from '../components/charts/AreaTrendCard';
import DonutCard from '../components/charts/DonutCard';
import PageHeader from '../components/common/PageHeader';
import ExportTableButton from '../components/common/ExportTableButton';
import axios from '../utils/axios';

/*
 * Admin → Conversations (/messages).
 *
 * Fed by GET /admin/dashboard/conversations?days=N, built for this page. It used
 * to read the generic dashboard analytics, whose redux thunk silently swapped in
 * invented demo numbers whenever the request failed, had no conversations
 * series (empty trend line) and showed all-time totals under a "last 30 days"
 * label. Errors are now shown as errors — never as fake data.
 */

const WINDOWS = [7, 30, 90, 180];
const int = (n) => Number(n || 0).toLocaleString('en-IN');
const when = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const mins = (m) => {
  if (m === null || m === undefined) return '—';
  if (m < 60) return `${m} min`;
  if (m < 60 * 24) return `${Math.round((m / 60) * 10) / 10} h`;
  return `${Math.round((m / 1440) * 10) / 10} days`;
};
const growthOf = (k) => (typeof k?.change === 'number' ? k.change : undefined);

const STATUS_COLOR = { new: 'default', contacted: 'info', negotiating: 'warning', won: 'success', lost: 'error' };
const OUTCOME = {
  delivered: { label: 'Delivered', color: 'success' },
  unmatched: { label: 'No seller found', color: 'error' },
  pending: { label: 'Pending', color: 'warning' },
  processed: { label: 'Processed', color: 'default' },
  closed_by_seller: { label: 'Closed by seller', color: 'default' },
  service_sub_category_missing: { label: 'Held: service missing', color: 'warning' },
  exhausted_no_engagement: { label: 'Expired (no seller acted)', color: 'default' },
};

const Head = ({ cols }) => (
  <TableHead>
    <TableRow>
      {cols.map((h) => (
        <TableCell key={h} sx={{ fontWeight: 800, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</TableCell>
      ))}
    </TableRow>
  </TableHead>
);
const Empty = ({ cols, text }) => (
  <TableRow><TableCell colSpan={cols} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>{text}</TableCell></TableRow>
);

const MessagesView = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [trendKey, setTrendKey] = useState('conversations');
  const [tab, setTab] = useState('conversations');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/admin/dashboard/conversations', { params: { days }, skipNotify: 'success' });
      if (!res?.data?.success) throw new Error(res?.data?.msg || 'Unexpected response');
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.msg || e?.message || 'Could not load conversations data.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const k = useMemo(() => data?.kpis || {}, [data]);
  const seriesMap = useMemo(() => ({
    conversations: data?.series?.conversations || [],
    messages: data?.series?.messages || [],
    enquiries: data?.series?.enquiries || [],
    leads: data?.series?.leads || [],
  }), [data]);
  const growth = useMemo(() => ({
    conversations: growthOf(k.conversations), messages: growthOf(k.messages),
    enquiries: growthOf(k.enquiries), leads: growthOf(k.leads),
  }), [k]);
  const spark = (key) => (data?.series?.[key] || []).map((p) => ({ v: p.count }));
  const vs = (x, fmt = int) => (x ? `vs ${fmt(x.prev)} previous ${days} days` : '');

  const recent = data?.recent || { conversations: [], leads: [], enquiries: [] };
  const showSkeleton = loading && !data;

  const tiles = [
    { label: 'Chats started', icon: <MessageCircle size={20} />, color: '#3B82F6', x: k.conversations, s: 'conversations' },
    { label: 'Chat messages', icon: <MessagesSquare size={20} />, color: '#6366F1', x: k.messages, s: 'messages' },
    { label: 'Seller reply rate', icon: <Reply size={20} />, color: '#10B981', x: k.replyRate, fmt: (v) => `${v || 0}%`, sec: data ? `${int(data.responsiveness.replied)} of ${int(data.responsiveness.started)} new chats · was ${k.replyRate?.prev ?? 0}%` : '' },
    { label: 'Median first reply', icon: <Timer size={20} />, color: '#F59E0B', x: k.medianFirstResponseMins, fmt: mins, noGrowth: true, sec: data ? `was ${mins(k.medianFirstResponseMins?.prev)}` : '' },
    { label: 'Buyer enquiries', icon: <Inbox size={20} />, color: '#8B5CF6', x: k.enquiries, s: 'enquiries', sec: data ? `${int(k.enquiriesUnmatched?.cur)} found no seller · ${vs(k.enquiries)}` : '' },
    { label: 'Leads delivered', icon: <Send size={20} />, color: '#06B6D4', x: k.leads, s: 'leads', sec: data ? `${k.leadsPerEnquiry?.cur ?? 0} per enquiry · ${int(k.leadsLocked?.cur)} locked (no quota)` : '' },
    { label: 'Lead win rate', icon: <Trophy size={20} />, color: '#059669', x: k.winRate, fmt: (v) => `${v || 0}%`, sec: data ? `won ÷ (won + lost) · was ${k.winRate?.prev ?? 0}%` : '' },
    { label: 'RFQs posted', icon: <FileText size={20} />, color: '#EC4899', x: k.rfqs, sec: data ? `${int(k.rfqQuotes?.cur)} quotes · ${int(k.savedSearches?.cur)} saved searches` : '' },
  ];

  return (
    <Box sx={{ py: 1, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <PageHeader
        title="Conversations & Leads"
        subtitle={data
          ? `Last ${days} days (${data.window.from} → ${data.window.to}), compared with the ${days} days before`
          : 'Buyer–seller chats, buyer enquiries and the leads delivered to sellers'}
        breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Conversations' }]}
        action={(
          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}
              sx={{ '& .MuiToggleButton-root': { px: 1.25, fontWeight: 700, textTransform: 'none' } }}
            >
              {WINDOWS.map((w) => <ToggleButton key={w} value={w}>{w}d</ToggleButton>)}
            </ToggleButtonGroup>
            <Tooltip title="Refresh">
              <span><IconButton aria-label="Refresh" onClick={load} disabled={loading}><RefreshCw size={18} /></IconButton></span>
            </Tooltip>
          </Stack>
        )}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error} No numbers are shown rather than showing figures that may be wrong.
        </Alert>
      )}

      {/* Needs-attention strip: "right now", not window-bound */}
      {data && (data.now.awaitingSeller > 0 || data.now.lockedOpen > 0) && (
        <Alert
          severity="warning" variant="outlined" sx={{ mt: 2, alignItems: 'center' }}
          action={<Button size="small" color="inherit" endIcon={<ArrowRight size={14} />} onClick={() => navigate('/buyer-seller-chat')}>Open chats</Button>}
        >
          <strong>{int(data.now.awaitingSeller)}</strong> chat{data.now.awaitingSeller === 1 ? ' is' : 's are'} waiting on a seller reply right now
          {data.now.lockedOpen > 0 && <> · <strong>{int(data.now.lockedOpen)}</strong> locked behind the seller's plan</>}
        </Alert>
      )}

      {(data || showSkeleton) && (
        <Box sx={{ opacity: loading && data ? 0.6 : 1, transition: 'opacity .2s' }}>
          <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mt: 2 }}>
            {tiles.map((t) => (
              <Grid key={t.label} size={{ xs: 12, sm: 6, lg: 3 }} sx={{ display: 'flex', '& > *': { flex: 1, minWidth: 0 } }}>
                {showSkeleton ? (
                  <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
                ) : (
                  <KpiTile
                    label={t.label}
                    value={(t.fmt || int)(t.x?.cur)}
                    icon={t.icon}
                    color={t.color}
                    growth={t.noGrowth ? undefined : growthOf(t.x)}
                    secondary={t.sec ?? vs(t.x)}
                    series={t.s ? spark(t.s) : [{ v: Number(t.x?.prev) || 0 }, { v: Number(t.x?.cur) || 0 }]}
                  />
                )}
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mt: { xs: 1, md: 2 } }}>
            <Grid size={{ xs: 12, lg: 8 }} sx={{ display: 'flex', '& > *': { flex: 1, minWidth: 0 } }}>
              {showSkeleton ? <Skeleton variant="rounded" height={360} sx={{ borderRadius: 4 }} /> : (
                <AreaTrendCard
                  title="Daily activity"
                  subtitle={`Per day (India time), last ${days} days`}
                  seriesMap={seriesMap}
                  growth={growth}
                  activeKey={trendKey}
                  onSelect={setTrendKey}
                  height={{ xs: 260, md: 320 }}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }} sx={{ display: 'flex', '& > *': { flex: 1, minWidth: 0 } }}>
              {showSkeleton ? <Skeleton variant="rounded" height={360} sx={{ borderRadius: 4 }} /> : (
                <DonutCard
                  title="Leads by channel"
                  subtitle={`Where the ${int(k.leads?.cur)} leads came from`}
                  data={(data.leadsBySource || []).filter((r) => r.cur > 0).map((r) => ({ name: r.name, count: r.cur }))}
                  height={240}
                />
              )}
            </Grid>
          </Grid>

          {data && (
            <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, mt: { xs: 2, md: 3 } }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Lead outcomes</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Status sellers set on leads delivered in the window (previous window in grey).
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1.5}>
                {(data.leadsByStatus.length ? data.leadsByStatus : [{ key: 'new', name: 'new', cur: 0, prev: 0 }]).map((s) => {
                  const total = k.leads?.cur || 0;
                  const pct = total ? Math.round((s.cur / total) * 100) : 0;
                  return (
                    <Box key={s.key} sx={{ flex: '1 1 140px', minWidth: 140, p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
                      <Chip size="small" label={s.key} color={STATUS_COLOR[s.key] || 'default'} variant="outlined" sx={{ textTransform: 'capitalize', fontWeight: 700 }} />
                      <Typography sx={{ fontWeight: 800, fontSize: 22, mt: 1 }}>{int(s.cur)}</Typography>
                      <Typography variant="caption" color="text.secondary">{pct}% of leads · was {int(s.prev)}</Typography>
                      <Box sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: alpha(theme.palette.grey[500], 0.15), overflow: 'hidden' }}>
                        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: 'primary.main' }} />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Card>
          )}

          {data && (
            <Card sx={{ borderRadius: 4, mt: { xs: 2, md: 3 }, overflow: 'hidden' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ px: { xs: 1, md: 2 }, pt: 1, gap: 1 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
                  <Tab value="conversations" label={`Recent chats (${int(data.now.totalConversations)})`} sx={{ fontWeight: 700, textTransform: 'none' }} />
                  <Tab value="leads" label={`Delivered leads (${int(data.now.totalLeads)})`} sx={{ fontWeight: 700, textTransform: 'none' }} />
                  <Tab value="enquiries" label="Buyer enquiries" sx={{ fontWeight: 700, textTransform: 'none' }} />
                </Tabs>
                <Stack direction="row" spacing={1} sx={{ px: 1, pb: { xs: 1, sm: 0 } }}>
                  {tab === 'enquiries' && (
                    <ExportTableButton
                      data={recent.enquiries}
                      total={k.enquiries?.cur || recent.enquiries.length}
                      onExportAll={async () => {
                        const res = await axios.get('/generalResellerEnquiry', { params: { all: true } });
                        return res.data?.enquiries || [];
                      }}
                      columns={[
                        { label: 'Name', value: (r) => r.buyer || `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'N/A' },
                        { label: 'Phone', key: 'phone' },
                        { label: 'Category', value: (r) => r.category || r.category_name || '' },
                        { label: 'City', key: 'city' },
                        { label: 'State', key: 'state' },
                        { label: 'Leads sent', value: (r) => r.leads_dispatched ?? 0 },
                        { label: 'Date', value: (r) => (r.created_at ? new Date(r.created_at).toLocaleString('en-IN') : '') },
                      ]}
                      filename="buyer_enquiries"
                    />
                  )}
                  <Button
                    size="small" endIcon={<ArrowRight size={14} />} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                    onClick={() => navigate(tab === 'conversations' ? '/buyer-seller-chat' : '/enquiries')}
                  >
                    {tab === 'conversations' ? 'All chats' : 'All enquiries'}
                  </Button>
                </Stack>
              </Stack>

              <TableContainer sx={{ overflowX: 'auto' }}>
                {tab === 'conversations' && (
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <Head cols={['Buyer', 'Seller', 'Product', 'Last message', 'When', 'Status']} />
                    <TableBody>
                      {recent.conversations.length === 0 && <Empty cols={6} text="No buyer–seller chats yet" />}
                      {recent.conversations.map((c) => (
                        <TableRow key={c._id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate('/buyer-seller-chat')}>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{c.buyer}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.seller}</TableCell>
                          <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.product || '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary' }} title={c.last_message}>{c.last_message || '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(c.last_message_at)}</TableCell>
                          <TableCell>
                            {c.seller_locked
                              ? <Chip size="small" label="Locked (plan)" color="warning" variant="outlined" />
                              : c.awaiting_seller
                                ? <Chip size="small" label="Awaiting seller" color="error" variant="outlined" />
                                : <Chip size="small" label="OK" variant="outlined" />}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {tab === 'leads' && (
                  <Table size="small" sx={{ minWidth: 760 }}>
                    <Head cols={['Buyer', 'Seller', 'Category', 'City', 'Channel', 'Status', 'When']} />
                    <TableBody>
                      {recent.leads.length === 0 && <Empty cols={7} text="No leads delivered yet" />}
                      {recent.leads.map((l) => (
                        <TableRow key={l._id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{l.buyer}</Typography>
                            <Typography variant="caption" color="text.secondary">{l.phone || '—'}</Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {l.seller}
                            {l.sellers_sent_count > 1 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>shared with {l.sellers_sent_count} sellers</Typography>}
                          </TableCell>
                          <TableCell>{l.category || '—'}</TableCell>
                          <TableCell>{l.city || '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.source}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Chip size="small" label={l.status} color={STATUS_COLOR[l.status] || 'default'} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                            {l.locked && <Chip size="small" label="Locked" color="warning" sx={{ ml: 0.5 }} />}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(l.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {tab === 'enquiries' && (
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <Head cols={['Buyer', 'Looking for', 'City', 'Type', 'Outcome', 'When']} />
                    <TableBody>
                      {recent.enquiries.length === 0 && <Empty cols={6} text="No buyer enquiries yet" />}
                      {recent.enquiries.map((e) => {
                        const o = OUTCOME[e.outcome] || { label: e.outcome, color: 'default' };
                        return (
                          <TableRow key={e._id} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{e.buyer}</Typography>
                              <Typography variant="caption" color="text.secondary">{e.phone || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                              {e.category || '—'}
                              {e.sub_category && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{e.sub_category}</Typography>}
                            </TableCell>
                            <TableCell>{[e.city, e.state].filter(Boolean).join(', ') || '—'}</TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>{e.type}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Chip size="small" label={o.label} color={o.color} variant="outlined" />
                              {e.leads_dispatched > 0 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{e.leads_dispatched} seller{e.leads_dispatched === 1 ? '' : 's'}</Typography>}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(e.created_at)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1.5 }}>
                Showing the 12 most recent. An enquiry is what a buyer submits; it is delivered to one or more sellers as leads.
              </Typography>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MessagesView;
